'use client';

import * as React from 'react';
import { useMemo } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

import { cn } from '../../lib/utils';

const Progress = React.forwardRef<
  React.ComponentRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      'relative h-2 w-full overflow-hidden rounded-full bg-primary/20',
      className,
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className="size-full flex-1 bg-primary transition-all"
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;

const CustomProgress = React.forwardRef<
  React.ComponentRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    direction?: 'left-to-right' | 'right-to-left';
    start?: number;
    end?: number;
  }
>(
  (
    {
      className,
      value,
      direction = 'left-to-right',
      start = 0,
      end = 100,
      ...props
    },
    ref,
  ) => {
    const { width, left } = useMemo(() => {
      // Ensure start and end are within valid bounds
      const normalizedStart = Math.min(Math.max(start, 0), 100);
      const normalizedEnd = Math.min(Math.max(end, normalizedStart), 100);

      // Clamp value between 0 and 100
      const currentValue = Math.min(Math.max(value ?? 0, 0), 100);

      if (direction === 'left-to-right') {
        return {
          width: currentValue,
          left: normalizedStart,
        };
      } else {
        return {
          width: currentValue,
          left: normalizedEnd - currentValue,
        };
      }
    }, [direction, value, start, end]);

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          'relative h-2 w-full overflow-hidden rounded-full bg-primary/20',
          className,
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="absolute h-full rounded-none bg-primary transition-all"
          style={{
            width: `${width}%`,
            left: `${left}%`,
          }}
        />
      </ProgressPrimitive.Root>
    );
  },
);
CustomProgress.displayName = ProgressPrimitive.Root.displayName;

export { Progress, CustomProgress };
